import type { DeviceAsset } from './asset'
import type { PageResponse } from './common'

export type CustomerStatus = 'PROSPECT' | 'ACTIVE' | 'SUSPENDED' | 'CHURNED'
export type CustomerContractStatus = 'DRAFT' | 'ACTIVE' | 'EXPIRING' | 'EXPIRED' | 'TERMINATED'

export interface Customer {
  id: number
  customerNo: string
  customerName: string
  shortName?: string
  unifiedSocialCreditCode?: string
  hospitalType?: string
  hospitalLevel?: string
  province?: string
  city?: string
  district?: string
  address?: string
  status: CustomerStatus
  customerManager?: string
  serviceManager?: string
  remark?: string
  deviceCount: number
  activeContractCount: number
  expiringContractCount: number
  createdAt: string
  updatedAt: string
}

export interface CustomerSite {
  id: number
  siteName: string
  siteCode?: string
  address?: string
  remark?: string
}

export interface CustomerContact {
  id: number
  siteId?: number
  contactName: string
  department?: string
  positionTitle?: string
  mobile?: string
  officePhone?: string
  email?: string
  contactRole?: string
  primaryContact: boolean
  notificationEnabled: boolean
  remark?: string
}

export interface CustomerContract {
  id: number
  contractNo: string
  contractName: string
  serviceType?: string
  serviceLevel?: string
  startDate?: string
  endDate?: string
  status: CustomerContractStatus
  coveredDeviceCount: number
  contractAmount?: number
  remark?: string
}

export interface CustomerDetail {
  customer: Customer
  sites: CustomerSite[]
  contacts: CustomerContact[]
  contracts: CustomerContract[]
  devices: DeviceAsset[]
  serviceRecords: CustomerServiceRecord[]
}

export interface CustomerServiceRecord {
  workOrderId: number
  orderNo: string
  title: string
  status: string
  deviceName?: string
  department?: string
  urgencyLevel?: string
  createdAt: string
}

export interface CustomerPageParams {
  keyword?: string
  status?: CustomerStatus
  page?: number
  size?: number
}

export type CustomerPageResponse = PageResponse<Customer>

export interface CreateCustomerRequest {
  customerName: string
  shortName?: string
  unifiedSocialCreditCode?: string
  hospitalType?: string
  hospitalLevel?: string
  province?: string
  city?: string
  district?: string
  address?: string
  status?: CustomerStatus
  customerManager?: string
  serviceManager?: string
  remark?: string
}

export interface CreateCustomerSiteRequest {
  siteName: string
  siteCode?: string
  address?: string
  remark?: string
}

export interface CreateCustomerContactRequest {
  siteId?: number
  contactName: string
  department?: string
  positionTitle?: string
  mobile?: string
  officePhone?: string
  email?: string
  contactRole?: string
  primaryContact: boolean
  notificationEnabled: boolean
  remark?: string
}

export interface CreateCustomerContractRequest {
  contractNo: string
  contractName: string
  serviceType?: string
  serviceLevel?: string
  startDate?: string
  endDate?: string
  status?: CustomerContractStatus
  coveredDeviceCount: number
  contractAmount?: number
  remark?: string
}

export const CUSTOMER_STATUS_LABELS: Record<CustomerStatus, string> = {
  PROSPECT: '潜在客户',
  ACTIVE: '合作中',
  SUSPENDED: '暂停服务',
  CHURNED: '已流失',
}

export const CONTRACT_STATUS_LABELS: Record<CustomerContractStatus, string> = {
  DRAFT: '草拟中',
  ACTIVE: '履约中',
  EXPIRING: '即将到期',
  EXPIRED: '已到期',
  TERMINATED: '已终止',
}
